export const styles = `
  /* Fonts are loaded via <link rel="preconnect"> + <link rel="stylesheet"> in public/index.html
     (earlier than a JS-injected @import, which only fired after the bundle parsed). */

  *, *::before, *::after {
    box-sizing: border-box; margin: 0; padding: 0;
    -webkit-tap-highlight-color: transparent;
  }
  button, a, [role="button"] { touch-action: manipulation; }

  html {
    -webkit-text-size-adjust: 100%;
    text-size-adjust: 100%;
  }

  :root {
    --bg: #0d0d0d;
    --surface: #161616;
    --surface2: #1f1f1f;
    --border: #2a2a2a;
    --border2: #333;
    --text: #e8e4dd;
    --text2: #888;
    --text3: #8a8a8a;
    --accent: #d4915c;
    --accent2: #e8b48a;
    --accent-dim: rgba(212,145,92,0.12);
    --green: #4caf82;
    --green-dim: rgba(76,175,130,0.12);
    --red: #e05c5c;
    --red-dim: rgba(224,92,92,0.12);
    --yellow: #d4b84a;
    --yellow-dim: rgba(212,184,74,0.12);
    --blue: #5c9ee0;
    --blue-dim: rgba(92,158,224,0.12);
    --sidebar-w: 220px;
    --ease-out-soft: cubic-bezier(0.16, 1, 0.3, 1);

    /* Fluid type/space scale */
    --text-xs: clamp(10px, 2vw, 12px);
    --text-sm: clamp(12px, 2.5vw, 13px);
    --text-base: clamp(13px, 3vw, 15px);
    --text-md: clamp(14px, 3.5vw, 16px);
    --text-lg: clamp(16px, 4vw, 20px);
    --text-xl: clamp(18px, 4.5vw, 24px);
    --text-2xl: clamp(20px, 5vw, 28px);
    --text-3xl: clamp(24px, 6vw, 36px);
    --space-xs: clamp(4px, 1vw, 8px);
    --space-sm: clamp(8px, 2vw, 12px);
    --space-md: clamp(12px, 3vw, 20px);
    --space-lg: clamp(16px, 4vw, 32px);
    --space-xl: clamp(24px, 5vw, 48px);
  }

  html, body {
    width: 100%; max-width: 100%; overflow-x: hidden;
  }
  @media (max-width: 768px) {
    html, body { overscroll-behavior: none; }
  }
  @media (min-width: 769px) {
    html, body { overscroll-behavior: auto; }
  }
  body {
    background: var(--bg); font-family: 'Geist', sans-serif; color: var(--text);
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
  .app {
    min-height: 100vh;
    min-height: 100dvh;
    min-height: -webkit-fill-available;
    display: flex; width: 100%;
  }

  /* ─── SIDEBAR ─── */
  .sidebar {
    width: var(--sidebar-w); flex-shrink: 0;
    background: var(--surface); border-right: 1px solid var(--border);
    position: fixed; top: 0; left: 0; bottom: 0;
    display: flex; flex-direction: column;
    z-index: 200; overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
    padding-top: env(safe-area-inset-top);
    padding-bottom: max(0px, env(safe-area-inset-bottom));
    padding-left: env(safe-area-inset-left);
  }
  .sidebar-logo {
    padding: 20px 18px 16px;
    font-family: 'Fraunces', serif; font-size: 20px;
    font-weight: 500; letter-spacing: -0.3px; color: var(--text);
    border-bottom: 1px solid var(--border);
  }
  .sidebar-logo em { color: var(--accent); font-style: italic; }
  .sidebar-mobile-close { display: none; }

  .sidebar-group { padding: 16px 12px 8px; }
  .sidebar-label {
    font-size: 10px; font-weight: 600; text-transform: uppercase;
    letter-spacing: 1.2px; color: var(--text3); padding: 0 8px; margin-bottom: 6px;
  }
  .sidebar-tabs { display: flex; flex-direction: column; gap: 2px; }

  .sidebar-tab {
    display: flex; align-items: center; gap: 10px;
    padding: 9px 12px; border-radius: 8px;
    font-size: 13px; font-weight: 400; color: var(--text2);
    cursor: pointer; border: none; background: transparent;
    font-family: 'Geist', sans-serif; transition: all 0.15s;
    text-align: left; width: 100%;
  }
  .sidebar-tab:hover { color: var(--text); background: var(--surface2); }
  .sidebar-tab.active { background: var(--accent-dim); color: var(--accent); font-weight: 500; }
  .sidebar-tab .tab-icon { font-size: 15px; width: 20px; text-align: center; flex-shrink: 0; }

  .sidebar-bottom {
    margin-top: auto; padding: 12px;
    border-top: 1px solid var(--border);
  }
  .agent-setting-label {
    font-size: 10px; font-weight: 600; text-transform: uppercase;
    letter-spacing: 1px; color: var(--text3); margin-bottom: 6px; padding: 0 4px;
  }
  .agent-name-row {
    display: flex; align-items: center; gap: 6px;
    background: var(--bg); border: 1px solid var(--border); border-radius: 7px;
    padding: 6px 10px;
  }
  .agent-name-row input {
    flex: 1; border: none; background: transparent; padding: 0;
    font-size: 13px; color: var(--text); outline: none;
    font-family: 'Geist', sans-serif;
  }
  .agent-name-row .pencil { font-size: 11px; color: var(--text3); flex-shrink: 0; }

  /* ─── MOBILE TOP BAR ─── */
  .mobile-bar {
    display: none;
    background: var(--surface); border-bottom: 1px solid var(--border);
    padding: 12px 16px; overflow-x: auto;
    position: sticky; top: 0; z-index: 200;
  }
  .mobile-bar-inner {
    display: flex; gap: 6px; min-width: max-content;
  }
  .mobile-pill {
    padding: 6px 14px; border-radius: 20px;
    border: 1px solid var(--border2); background: transparent;
    font-size: 12px; color: var(--text2); cursor: pointer;
    font-family: 'Geist', sans-serif; white-space: nowrap; transition: all 0.15s;
  }
  .mobile-pill.active { background: var(--accent-dim); border-color: var(--accent); color: var(--accent); font-weight: 500; }

  /* ─── MAIN CONTENT ─── */
  .main-content {
    margin-left: var(--sidebar-w); flex: 1; min-width: 0;
  }
  @media (min-width: 769px) {
    html, body, #root, .app, .layout {
      overflow: visible !important;
    }
    .main-content {
      overflow-y: auto !important;
      height: 100vh;
      height: 100dvh;
      overscroll-behavior: auto !important;
      pointer-events: auto !important;
    }
    .sidebar-backdrop {
      display: none !important;
      pointer-events: none !important;
    }
  }

  .layout {
    display: flex; flex: 1;
    max-width: 1080px; margin: 0 auto;
    width: 100%; padding: 32px 24px; gap: 28px;
  }

  .input-panel { width: 340px; flex-shrink: 0; display: flex; flex-direction: column; gap: 12px; }

  .panel-title {
    font-family: 'Fraunces', serif; font-size: 22px;
    font-weight: 500; letter-spacing: -0.3px; color: var(--text);
    margin-bottom: 4px;
  }
  .panel-sub { font-size: 13px; color: var(--text2); line-height: 1.5; margin-bottom: 4px; }

  .section { background: var(--surface); border: 1px solid var(--border); border-radius: 12px; overflow: visible; }
  .section > .section-head:first-child { border-radius: 12px 12px 0 0; }

  .section-head {
    padding: 12px 16px; border-bottom: 1px solid var(--border);
    font-size: 11px; font-weight: 600; text-transform: uppercase;
    letter-spacing: 1px; color: var(--text3);
    display: flex; align-items: center; justify-content: space-between;
    position: relative; z-index: 10;
  }

  .section-body { padding: 16px; display: flex; flex-direction: column; gap: 12px; }

  .field { display: flex; flex-direction: column; gap: 5px; }
  .field label { font-size: 11px; font-weight: 500; color: var(--text2); letter-spacing: 0.3px; }
  .field.required-miss input,
  .field.required-miss textarea { border-left: 3px solid var(--red); }
  .row { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }

  input, select, textarea {
    font-family: 'Geist', sans-serif; font-size: 13px;
    color: var(--text); background: var(--bg);
    border: 1px solid var(--border); border-radius: 7px;
    padding: 8px 11px; outline: none;
    transition: border-color 0.15s, background 0.15s, opacity 0.15s; width: 100%;
    color-scheme: dark;
  }
  input:focus, select:focus, textarea:focus { border-color: var(--accent); background: var(--surface2); }
  input::placeholder, textarea::placeholder { color: var(--text3); }
  input:disabled, textarea:disabled { opacity: 0.5; cursor: not-allowed; }
  textarea { resize: none; min-height: 72px; line-height: 1.5; }


  /* Custom scrollbar */
  ::-webkit-scrollbar { width: 6px; height: 6px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: var(--border2); border-radius: 3px; }
  ::-webkit-scrollbar-thumb:hover { background: var(--text3); }
  * { scrollbar-width: thin; scrollbar-color: var(--border2) transparent; }

  .pills { display: flex; flex-wrap: wrap; gap: 6px; }

  .pill {
    padding: 5px 11px; border-radius: 20px;
    border: 1px solid var(--border2); background: transparent;
    font-size: 12px; color: var(--text2); cursor: pointer;
    font-family: 'Geist', sans-serif; transition: all 0.15s;
  }
  .pill:hover { border-color: var(--accent); color: var(--accent); }
  .pill.on { background: var(--accent-dim); border-color: var(--accent); color: var(--accent); font-weight: 500; }

  /* CUSTOM TONE */
  .custom-tone-wrap {
    overflow: hidden; max-height: 0;
    transition: max-height 0.25s ease, opacity 0.25s ease; opacity: 0;
  }
  .custom-tone-wrap.open { max-height: 120px; opacity: 1; }

  .custom-tone-input { display: flex; gap: 8px; align-items: center; margin-top: 10px; }
  .custom-tone-input input { flex: 1; font-size: 13px; }

  .apply-btn {
    padding: 7px 13px; border-radius: 7px;
    border: 1px solid var(--accent); background: var(--accent-dim);
    color: var(--accent); font-size: 12px; font-weight: 600;
    cursor: pointer; font-family: 'Geist', sans-serif;
    white-space: nowrap; transition: all 0.15s;
  }
  .apply-btn:hover { background: var(--accent); color: #0d0d0d; }

  .tone-hint { font-size: 11px; color: var(--text3); margin-top: 6px; line-height: 1.5; }

  .custom-badge {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 4px 10px; border-radius: 20px;
    background: var(--accent-dim); border: 1px solid var(--accent);
    font-size: 11px; color: var(--accent); font-weight: 500; margin-top: 8px;
  }
  .custom-badge button {
    background: none; border: none; color: var(--accent);
    cursor: pointer; font-size: 13px; padding: 0; line-height: 1;
    font-family: 'Geist', sans-serif;
  }

  .gen-btn {
    width: 100%; padding: 12px; background: var(--accent);
    color: #0d0d0d; border: none; border-radius: 10px;
    font-family: 'Geist', sans-serif; font-size: 14px;
    font-weight: 600; cursor: pointer; transition: all 0.15s;
    display: flex; align-items: center; justify-content: center; gap: 8px;
  }
  .gen-btn:hover:not(:disabled) { background: var(--accent2); transform: translateY(-1px); }
  .gen-btn:disabled { background: var(--surface2); color: var(--text3); cursor: not-allowed; transform: none; }
  .gen-btn.muted { background: var(--surface2); color: var(--text3); }

  .spinner {
    width: 14px; height: 14px;
    border: 2px solid rgba(13,13,13,0.25); border-top-color: #0d0d0d;
    border-radius: 50%; animation: spin 0.7s linear infinite;
  }
  @keyframes spin { to { transform: rotate(360deg); } }

  .output-panel { flex: 1; min-width: 0; }
  .output-panel.pulsing { animation: pulseBorder 1.5s ease infinite; }
  @keyframes pulseBorder {
    0%, 100% { outline: 2px solid transparent; outline-offset: 2px; border-radius: 14px; }
    50% { outline: 2px solid var(--accent); outline-offset: 2px; border-radius: 14px; }
  }

  .output-empty, .empty-output {
    height: 100%; min-height: 400px;
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    border: 1px dashed var(--border2); border-radius: 14px;
    color: var(--text3); text-align: center; padding: 40px;
  }
  .empty-glyph, .empty-icon {
    font-family: 'Fraunces', serif; font-size: 48px;
    font-style: italic; color: var(--border2); margin-bottom: 16px; line-height: 1;
  }
  .empty-title { font-size: 15px; color: var(--text2); margin-bottom: 6px; font-weight: 500; }
  .empty-sub { font-size: 13px; color: var(--text3); }

  .result { display: flex; flex-direction: column; gap: 14px; animation: fadeUp 0.35s ease; }
  @keyframes fadeUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }

  .otabs {
    display: flex; gap: 2px; background: var(--surface);
    border: 1px solid var(--border); border-radius: 10px; padding: 4px;
  }
  .otab {
    flex: 1; padding: 7px 10px; border-radius: 7px;
    font-size: 12px; font-weight: 500; color: var(--text2);
    cursor: pointer; border: none; background: transparent;
    font-family: 'Geist', sans-serif; transition: all 0.15s; text-align: center;
  }
  .otab:hover { color: var(--text); }
  .otab.on { background: var(--surface2); color: var(--text); border: 1px solid var(--border2); }

  .outbox { background: var(--surface); border: 1px solid var(--border); border-radius: 12px; overflow: hidden; }

  .outbox-header {
    padding: 12px 16px; border-bottom: 1px solid var(--border);
    display: flex; align-items: center; justify-content: space-between;
  }
  .outbox-label { display: flex; align-items: center; gap: 7px; font-size: 12px; font-weight: 500; color: var(--text2); }
  .status-dot { width: 7px; height: 7px; border-radius: 50%; background: var(--green); box-shadow: 0 0 6px var(--green); }

  .action-row { display: flex; gap: 6px; }
  .act-btn {
    padding: 4px 11px; border-radius: 6px; border: 1px solid var(--border2);
    background: transparent; font-size: 12px; font-weight: 500;
    color: var(--text2); cursor: pointer; font-family: 'Geist', sans-serif; transition: all 0.15s;
  }
  .act-btn:hover { border-color: var(--text2); color: var(--text); }
  .act-btn.green { border-color: var(--green); color: var(--green); background: var(--green-dim); }

  .outbox-body { padding: 22px; }
  .out-text { font-size: 14px; line-height: 1.85; color: var(--text); white-space: pre-wrap; font-weight: 300; }
  .out-subject { font-size: 13px; color: var(--text2); font-weight: 600; margin-bottom: 8px; }
  .out-meta { font-size: 11px; color: var(--text3); margin-top: 12px; padding-top: 12px; border-top: 1px solid var(--border); }
  .error-bar { background: var(--red-dim); border: 1px solid var(--red); border-radius: 8px; padding: 11px 14px; margin-bottom: 12px; font-size: 13px; color: var(--red); }

  /* MLS paragraph formatting */
  .mls-paragraphs { display: flex; flex-direction: column; gap: 0; }
  .mls-para { font-size: 14px; line-height: 1.85; color: var(--text); font-weight: 300; padding: 14px 0; }
  .mls-para:first-child { padding-top: 0; }
  .mls-para:last-child { padding-bottom: 0; }
  .mls-para + .mls-para { border-top: 1px solid var(--border); }
  /* Word-by-word output reveal (feature G) — cascading opacity fade on generated copy. Per-word
     animation-delay is set inline; reduced motion forces the final state (the global reduced-motion
     block zeroes animation-duration but NOT animation-delay, so this explicit override is required). */
  .reveal-words .rw { opacity: 0; animation: revealWord 0.45s var(--ease-out-soft) forwards; }
  @keyframes revealWord { from { opacity: 0; } to { opacity: 1; } }
  @media (prefers-reduced-motion: reduce) { .reveal-words .rw { opacity: 1 !important; animation: none !important; } }

  .meta-row { display: flex; gap: 16px; margin-top: 16px; padding-top: 14px; border-top: 1px solid var(--border); }
  .meta-item { font-size: 11px; color: var(--text3); }
  .meta-item span { color: var(--text2); font-weight: 500; }

  .improve-row { display: flex; gap: 8px; flex-wrap: wrap; }
  .improve-btn {
    padding: 6px 12px; border-radius: 6px; border: 1px solid var(--border2);
    background: var(--surface2); font-size: 12px; color: var(--text2);
    cursor: pointer; font-family: 'Geist', sans-serif; transition: all 0.15s;
  }
  .improve-btn:hover { border-color: var(--accent); color: var(--accent); }

  /* EMAILS */
  .email-list { display: flex; flex-direction: column; gap: 10px; }
  .email-card { background: var(--surface); border: 1px solid var(--border); border-radius: 10px; overflow: hidden; }
  .email-card:hover { border-color: var(--border2); }
  .email-top {
    padding: 10px 14px; border-bottom: 1px solid var(--border);
    display: flex; align-items: center; justify-content: space-between; background: var(--bg);
  }
  .email-badge { display: flex; align-items: center; gap: 8px; }
  .badge-num {
    width: 20px; height: 20px; border-radius: 50%;
    background: var(--accent-dim); border: 1px solid var(--accent);
    color: var(--accent); font-size: 10px; font-weight: 700;
    display: flex; align-items: center; justify-content: center;
  }
  .badge-tag { font-size: 11px; font-weight: 500; color: var(--text2); }
  .email-body { padding: 14px; }
  .email-subj { font-size: 12px; font-weight: 600; color: var(--text2); margin-bottom: 10px; }
  .email-subj span { color: var(--text); }
  .email-txt { font-size: 13px; line-height: 1.75; color: var(--text); white-space: pre-wrap; font-weight: 300; }

  /* CONTRACT */
  .contract-list { display: flex; flex-direction: column; }
  .contract-row { display: flex; gap: 14px; align-items: flex-start; padding: 14px 0; border-bottom: 1px solid var(--border); }
  .contract-row:last-child { border-bottom: none; }
  .ctag { font-size: 10px; font-weight: 600; letter-spacing: 0.5px; padding: 3px 8px; border-radius: 4px; white-space: nowrap; flex-shrink: 0; margin-top: 2px; }
  .ctag-date { background: var(--blue-dim); color: var(--blue); border: 1px solid rgba(92,158,224,0.2); }
  .ctag-contingency { background: var(--yellow-dim); color: var(--yellow); border: 1px solid rgba(212,184,74,0.2); }
  .ctag-flag { background: var(--red-dim); color: var(--red); border: 1px solid rgba(224,92,92,0.2); }
  .ctag-term { background: var(--green-dim); color: var(--green); border: 1px solid rgba(76,175,130,0.2); }
  .crow-label { font-size: 12px; font-weight: 600; color: var(--text2); margin-bottom: 3px; }
  .crow-text { font-size: 13px; color: var(--text); line-height: 1.6; font-weight: 300; }

  .update-btn {
    padding: 3px 9px; border-radius: 5px;
    border: 1px solid var(--accent); background: var(--accent-dim);
    color: var(--accent); font-size: 10px; font-weight: 600;
    cursor: pointer; font-family: 'Geist', sans-serif; transition: all 0.15s;
    text-transform: uppercase; letter-spacing: 0.5px;
  }
  .update-btn:hover { background: var(--accent); color: #0d0d0d; }
  .update-btn.saved { border-color: var(--green); background: var(--green-dim); color: var(--green); }
  .synced-hint { font-size: 11px; color: var(--text3); margin-top: 2px; font-weight: 400; }
  .synced-hint span { color: var(--accent); }

  .shimmer-box { background: var(--surface); border: 1px solid var(--border); border-radius: 12px; padding: 24px; display: flex; flex-direction: column; gap: 10px; }
  .shimmer { height: 14px; border-radius: 4px; background: linear-gradient(90deg, var(--border) 25%, var(--border2) 50%, var(--border) 75%); background-size: 200% 100%; animation: shimmer 1.2s ease infinite; }
  @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
  .shimmer-label { text-align: center; font-size: 12px; color: var(--text3); margin-top: 8px; font-weight: 400; }

  /* ─── OUTPUT HISTORY ─── */
  .history-section { margin-top: 14px; }
  .history-toggle {
    display: flex; align-items: center; gap: 6px;
    background: none; border: none; cursor: pointer;
    font-family: 'Geist', sans-serif; font-size: 12px;
    color: var(--text3); font-weight: 500; padding: 8px 0;
    transition: color 0.15s;
  }
  .history-toggle:hover { color: var(--text2); }
  .history-arrow { font-size: 10px; transition: transform 0.2s; }
  .history-arrow.open { transform: rotate(90deg); }
  .history-list { display: flex; flex-direction: column; gap: 6px; margin-top: 6px; }
  .history-item {
    display: flex; align-items: center; gap: 8px;
    padding: 8px 12px; background: var(--surface);
    border: 1px solid var(--border); border-radius: 8px;
    cursor: pointer; transition: all 0.15s;
  }
  .history-item:hover { border-color: var(--border2); }
  .history-preview { flex: 1; font-size: 12px; color: var(--text2); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .history-time { font-size: 10px; color: var(--text3); white-space: nowrap; }
  .history-delete {
    background: none; border: none; cursor: pointer;
    font-size: 13px; color: var(--text3); padding: 0 2px;
    font-family: 'Geist', sans-serif; transition: color 0.15s;
  }
  .history-delete:hover { color: var(--red); }

  /* ─── DASHBOARD MOBILE TOGGLE ─── */
  .mobile-sidebar-toggle {
    display: none;
    position: fixed;
    top: max(12px, env(safe-area-inset-top));
    left: max(12px, env(safe-area-inset-left));
    z-index: 250;
    width: 44px; height: 44px;
    background: var(--surface); border: 1px solid var(--border);
    border-radius: 10px; color: var(--text); font-size: 22px;
    align-items: center; justify-content: center; cursor: pointer;
    font-family: 'Geist', sans-serif;
  }
  .sidebar-backdrop {
    display: none;
    position: fixed; inset: 0; background: rgba(0,0,0,0.6);
    z-index: 199; opacity: 0; pointer-events: none;
    transition: opacity 0.25s ease;
  }
  .sidebar-backdrop.open { opacity: 1; pointer-events: auto; }

  /* ─── MOBILE RESPONSIVE ─── */
  @media (max-width: 768px) {
    :root { --sidebar-w: 0px; }
    body { font-size: 14px; line-height: 1.6; }

    /* Sidebar drawer */
    .sidebar {
      display: flex;
      width: 280px; max-width: 85vw;
      transform: translateX(-100%);
      transition: transform 0.28s var(--ease-out-soft);
      box-shadow: 0 0 24px rgba(0,0,0,0.4);
    }
    .sidebar.open { transform: translateX(0); }
    .sidebar-tab { padding: 14px 14px; font-size: 14px; min-height: 48px; }
    .sidebar-tab .tab-icon { font-size: 17px; }
    .sidebar-account-tab { padding: 14px 14px !important; font-size: 14px !important; min-height: 48px; }
    .mobile-sidebar-toggle { display: flex; }
    .sidebar-backdrop { display: block; }
    .mobile-bar { display: none; }
    .sidebar-mobile-close { display: flex; align-items: center; justify-content: center; }
    .sidebar-logo { padding: 0 16px 16px 16px; border-bottom: 1px solid var(--border); }

    /* Main content */
    .main-content { margin-left: 0; padding-top: 64px; }
    .layout { flex-direction: column; padding: 14px; gap: 16px; max-width: 100%; }
    .input-panel { width: 100%; }
    .row { grid-template-columns: 1fr; }
    .output-panel { width: 100%; }

    /* Inputs / forms — !important defeats inline fontSize:12/13/14 (which beat a plain
       stylesheet rule) so iOS Safari never auto-zooms on focus, app-wide. Mobile-only;
       desktop (>768px) is untouched. */
    input:not([type="checkbox"]):not([type="radio"]), select, textarea { font-size: 16px !important; padding: 12px 14px; min-height: 44px; }
    .field label { font-size: 12px; }
    textarea { min-height: 96px; }

    /* Generate button */
    .gen-btn { padding: 16px; min-height: 52px; font-size: 15px; }

    /* Tone pills */
    .pill { padding: 9px 14px; font-size: 13px; min-height: 36px; }
    .pills { gap: 8px; }
    .apply-btn { padding: 10px 14px; min-height: 40px; }

    /* Output tabs scroll horizontally */
    .otabs { overflow-x: auto; flex-wrap: nowrap; -webkit-overflow-scrolling: touch; }
    .otab { flex: 0 0 auto; min-width: 80px; padding: 12px 16px; font-size: 13px; min-height: 44px; }

    /* Action buttons (copy etc) */
    .action-row { flex-wrap: wrap; gap: 8px; }
    .act-btn { padding: 10px 14px; font-size: 13px; min-height: 40px; flex: 1 1 auto; }

    /* Output content — slightly larger line-height for comfortable reading on a phone */
    .outbox-header { flex-wrap: wrap; gap: 10px; padding: 14px; }
    .outbox-body { padding: 16px; }
    .out-text { font-size: 15px; line-height: 1.9; }
    .mls-para { font-size: 15px; line-height: 1.9; }
    .email-txt { font-size: 14px; line-height: 1.85; }

    /* Improve buttons */
    .improve-row { gap: 8px; }
    .improve-btn { padding: 10px 14px; font-size: 13px; min-height: 40px; }

    /* Panel headings scale down */
    .panel-title { font-size: 19px; }
    .panel-sub { font-size: 13px; }

    /* Paywall mobile */
    .paywall { padding: 80px 16px 40px; }
    .paywall-headline { font-size: 28px; }
    .paywall-sub { font-size: 14px; margin-bottom: 32px; }
    .paywall-logo { font-size: 24px; margin-bottom: 32px; }
    .plan-cards { flex-direction: column; gap: 16px; width: 100%; align-items: stretch; }
    .plan-card { width: 100%; max-width: none; padding: 24px; }
    .plan-cta { padding: 16px; min-height: 52px; font-size: 15px; }

    /* Auth screen mobile — show ONLY the form by default */
    .auth-page {
      display: block; min-height: 100vh; width: 100%;
      background: var(--bg); padding: 0; margin: 0;
    }
    .auth-left {
      display: none;
      width: 100%; padding: 32px 20px;
      border-right: none; border-top: 1px solid var(--border);
      box-sizing: border-box;
    }
    .auth-page.show-plans .auth-left { display: flex; flex-direction: column; }
    .auth-right {
      display: flex; align-items: flex-start; justify-content: center;
      width: 100%; min-height: 100vh; padding: 80px 16px 32px;
      box-sizing: border-box;
    }
    .auth-card {
      width: 90%; max-width: 400px; margin: 0 auto;
      padding: 24px; box-sizing: border-box;
    }
    .auth-card input:not([type="checkbox"]):not([type="radio"]),
    .auth-card select,
    .auth-card textarea {
      width: 100%; box-sizing: border-box;
      font-size: 16px !important; min-height: 48px; padding: 12px 14px;
    }
    .auth-card input[type="checkbox"] {
      width: 18px !important; height: 18px !important;
      min-width: 18px; min-height: 18px;
      padding: 0 !important; margin: 2px 0 0 0 !important;
      flex-shrink: 0;
    }
    .auth-btn { min-height: 52px; font-size: 16px; padding: 14px; }
    .auth-hero { font-size: 28px; line-height: 1.2; }
    .auth-subhero { font-size: 14px; }
    .auth-pricing { flex-direction: column; }
    .auth-see-plans {
      display: block;
      width: 90%; max-width: 400px; margin: 18px auto 0;
      padding: 12px; min-height: 44px;
      background: transparent; color: var(--text3);
      border: none; border-radius: 8px;
      font-size: 13px; font-family: 'Geist', sans-serif; cursor: pointer;
      text-align: center;
    }
    .auth-see-plans:hover, .auth-see-plans:active { color: var(--accent); }

    /* Hamburger button — bigger touch target */
    .hamburger-btn {
      width: 44px; height: 44px; font-size: 24px;
      top: 12px; left: 12px;
    }

    /* Hamburger menu — full screen on mobile */
    .hmenu-panel { max-height: 100vh; height: 100vh; }
    .hmenu-body { padding: 24px 16px; }
    .hmenu-header { padding: 18px 16px; }
    .hmenu-close { width: 44px; height: 44px; font-size: 28px; }
    .hmenu-tabs { padding: 0 8px; overflow-x: auto; -webkit-overflow-scrolling: touch; }
    .hmenu-tab { padding: 16px 14px; font-size: 13px; white-space: nowrap; min-height: 48px; }
    .hmenu-features-grid { grid-template-columns: 1fr; }
    .hmenu-heading { font-size: 22px; }
    .hmenu-text { font-size: 14px; }
    .hmenu-cta { width: 100%; padding: 16px; min-height: 52px; }
    .hmenu-form-btn { width: 100%; padding: 16px; min-height: 52px; align-self: stretch; }
    .hmenu-stat { min-width: 100%; }

    /* Plans comparison table mobile */
    .hmenu-compare-wrap {
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
      margin: 0 -16px;
      padding: 0 16px;
    }
    .hmenu-compare {
      font-size: 11px;
      min-width: 480px;
    }
    .hmenu-compare th {
      padding: 8px 6px;
      font-size: 9px;
      letter-spacing: 0.3px;
    }
    .hmenu-compare td {
      padding: 8px 6px;
    }

    /* Account page mobile */
    .acct-cards { gap: 14px; }
    .acct-btn { padding: 14px; min-height: 48px; font-size: 14px; width: 100%; }
    .acct-save-btn { width: 100%; max-width: none; padding: 14px; min-height: 48px; font-size: 14px; }
    .acct-profile-card { padding: 20px; }

    /* Section headers a bit roomier */
    .section-head { padding: 14px 16px; }
    .section-body { padding: 16px; gap: 14px; }

    /* History items — bigger row + a reachable delete tap target on mobile */
    .history-item { padding: 12px; min-height: 48px; }
    .history-delete { min-width: 40px; min-height: 40px; padding: 8px; font-size: 16px; display: inline-flex; align-items: center; justify-content: center; }

    /* Email send buttons (Open in Email/Gmail/Outlook) — full-width block buttons; bump to a
       44px tap target on mobile. !important beats their inline padding. */
    .email-send-btn { min-height: 44px !important; padding: 12px 16px !important; }

    /* Prevent horizontal scroll */
    html, body { overflow-x: hidden; max-width: 100vw; }
  }

  @media (max-width: 428px) {
    .paywall-headline { font-size: 26px; }
    .auth-hero { font-size: 26px; }
    .layout { padding: 12px; }
    /* Property Info field grids stack to one column on phones — two money/text inputs side
       by side are too cramped. !important overrides the inline gridTemplateColumns. */
    .pi-grid { grid-template-columns: 1fr !important; }
  }

  /* ─── USAGE CARD ─── */
  .usage-card {
    margin: 0 12px 8px; padding: 12px;
    background: var(--bg); border: 1px solid var(--border);
    border-radius: 8px;
  }
  .usage-card-label {
    font-size: 10px; font-weight: 600; text-transform: uppercase;
    letter-spacing: 1px; color: var(--text3); margin-bottom: 8px;
  }
  .usage-card-count { font-size: 18px; font-weight: 600; color: var(--text); margin-bottom: 6px; }
  .usage-card-count span { font-size: 12px; font-weight: 400; color: var(--text3); }
  .usage-bar { height: 4px; border-radius: 2px; background: var(--border); overflow: hidden; }
  .usage-bar-fill { height: 100%; border-radius: 2px; transition: width 0.3s, background 0.3s; }
  .usage-bar-fill.ok { background: var(--accent); }
  .usage-bar-fill.low { background: var(--red); }
  .upgrade-msg {
    margin-top: 8px; font-size: 11px; color: var(--red); line-height: 1.4;
  }
  .upgrade-btn {
    margin-top: 6px; width: 100%; padding: 7px; border-radius: 6px;
    border: 1px solid var(--accent); background: var(--accent-dim);
    color: var(--accent); font-size: 11px; font-weight: 600;
    cursor: pointer; font-family: 'Geist', sans-serif; transition: all 0.15s;
  }
  .upgrade-btn:hover { background: var(--accent); color: #0d0d0d; }

  /* ─── ROW-3 for 3-column grid ─── */
  .row-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 10px; }
  @media (max-width: 768px) { .row-3 { grid-template-columns: 1fr; } }

  /* ─── GOOGLE PLACES AUTOCOMPLETE DARK THEME ─── */
  .pac-container {
    background: #161616 !important;
    border: 1px solid #2a2a2a !important;
    border-radius: 8px !important;
    box-shadow: 0 8px 24px rgba(0,0,0,0.5) !important;
    margin-top: 4px !important;
    font-family: 'Geist', sans-serif !important;
  }
  .pac-item {
    padding: 8px 12px !important;
    border-top: 1px solid #2a2a2a !important;
    color: #e8e4dd !important;
    font-size: 13px !important;
    cursor: pointer !important;
  }
  .pac-item:first-child { border-top: none !important; }
  .pac-item:hover, .pac-item-selected {
    background: rgba(212,145,92,0.12) !important;
  }
  .pac-item-query { color: #e8e4dd !important; font-weight: 500 !important; }
  .pac-matched { color: #d4915c !important; font-weight: 600 !important; }
  .pac-item span:not(.pac-item-query):not(.pac-matched) { color: #888 !important; }
  .pac-icon, .hdpi .pac-icon { display: none !important; }
  .pac-logo::after { display: none !important; }

  /* ─── PAYWALL ─── */
  .paywall {
    min-height: 100vh; background: var(--bg);
    display: flex; flex-direction: column; align-items: center;
    padding: 60px 24px;
  }
  .paywall-logo {
    font-family: 'Fraunces', serif; font-size: 28px;
    font-weight: 500; letter-spacing: -0.3px; color: var(--text);
    margin-bottom: 48px; align-self: flex-start;
  }
  .paywall-logo em { color: var(--accent); font-style: italic; }
  .paywall-headline {
    font-family: 'Fraunces', serif; font-size: 40px;
    font-weight: 500; letter-spacing: -0.5px; color: var(--text);
    text-align: center; margin-bottom: 12px; max-width: 600px; line-height: 1.2;
  }
  .paywall-sub {
    font-size: 16px; color: var(--text2); text-align: center;
    margin-bottom: 48px; max-width: 500px; line-height: 1.6;
  }
  .plan-cards { display: flex; gap: 24px; margin-bottom: 32px; flex-wrap: wrap; justify-content: center; }
  .plan-card {
    width: 320px; background: var(--surface);
    border: 1px solid var(--border); border-radius: 16px;
    padding: 32px; display: flex; flex-direction: column;
    position: relative; transition: border-color 0.2s;
  }
  .plan-card:hover { border-color: var(--border2); }
  .plan-card.featured { border-color: var(--accent); }
  .plan-badge {
    position: absolute; top: -12px; left: 50%; transform: translateX(-50%);
    background: var(--accent); color: #0d0d0d;
    padding: 4px 14px; border-radius: 20px;
    font-size: 11px; font-weight: 700; text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  .plan-name {
    font-family: 'Fraunces', serif; font-size: 24px;
    font-weight: 500; color: var(--text); margin-bottom: 8px;
  }
  .plan-price { font-size: 36px; font-weight: 600; color: var(--text); margin-bottom: 4px; }
  .plan-price span { font-size: 14px; font-weight: 400; color: var(--text2); }
  .plan-features { list-style: none; margin: 20px 0 28px; display: flex; flex-direction: column; gap: 10px; }
  .plan-features li {
    font-size: 13px; color: var(--text2); display: flex; align-items: center; gap: 8px;
  }
  .plan-features li::before { content: "✓"; color: var(--accent); font-weight: 700; font-size: 14px; }
  .plan-cta {
    width: 100%; padding: 12px; border-radius: 10px;
    font-family: 'Geist', sans-serif; font-size: 14px;
    font-weight: 600; cursor: pointer; transition: all 0.15s;
    display: flex; align-items: center; justify-content: center;
    margin-top: auto; border: none;
  }
  .plan-cta.primary { background: var(--accent); color: #0d0d0d; }
  .plan-cta.primary:hover { background: var(--accent2); transform: translateY(-1px); }
  .plan-cta.secondary { background: var(--surface2); color: var(--text); border: 1px solid var(--border2); }
  .plan-cta.secondary:hover { border-color: var(--accent); color: var(--accent); }
  .paywall-fine { font-size: 12px; color: var(--text3); text-align: center; }
  .manage-link {
    display: block; margin-top: 10px; padding: 0 4px;
    font-size: 11px; color: var(--text3); text-decoration: none;
    cursor: pointer; background: none; border: none;
    font-family: 'Geist', sans-serif; transition: color 0.15s;
  }
  .manage-link:hover { color: var(--accent); }

  /* ─── SUCCESS PAGE ─── */
  .success-page {
    min-height: 100vh; background: var(--bg);
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    text-align: center; padding: 40px;
  }
  .success-icon { font-size: 64px; margin-bottom: 20px; }
  .success-title {
    font-family: 'Fraunces', serif; font-size: 28px;
    font-weight: 500; color: var(--text); margin-bottom: 8px;
  }
  .success-sub { font-size: 14px; color: var(--text2); margin-bottom: 4px; }

  /* ─── AUTH SCREEN — SPLIT LAYOUT ─── */
  .auth-page {
    min-height: 100vh; display: flex; background: var(--bg);
  }
  .auth-see-plans { display: none; }
  .auth-left {
    width: 55%; padding: 48px 56px; display: flex; flex-direction: column;
    justify-content: center; border-right: 1px solid var(--border);
    overflow-y: auto;
  }
  .auth-right {
    width: 45%; display: flex; align-items: center; justify-content: center;
    padding: 48px 40px;
  }

  .auth-logo {
    font-family: 'Fraunces', serif; font-size: 32px; font-weight: 500;
    letter-spacing: -0.3px; color: var(--text); margin-bottom: 28px;
  }
  .auth-logo em { color: var(--accent); font-style: italic; }

  .auth-hero {
    font-family: 'Fraunces', serif; font-size: 34px; font-weight: 500;
    line-height: 1.25; color: var(--text); margin-bottom: 12px;
    letter-spacing: -0.5px; max-width: 480px;
  }
  .auth-subhero {
    font-size: 15px; color: var(--text2); line-height: 1.6;
    margin-bottom: 32px; max-width: 460px;
  }

  .auth-features { display: flex; flex-direction: column; gap: 10px; margin-bottom: 36px; }
  .auth-feature {
    display: flex; align-items: flex-start; gap: 10px;
    font-size: 13px; color: var(--text2); line-height: 1.4;
  }
  .auth-feature-icon {
    color: var(--accent); font-size: 13px; flex-shrink: 0; margin-top: 1px;
  }
  .auth-feature strong { color: var(--text); font-weight: 500; }

  .auth-demo { margin-bottom: 36px; }
  .auth-demo-label {
    font-size: 10px; font-weight: 600; text-transform: uppercase;
    letter-spacing: 1.2px; color: var(--text3); margin-bottom: 12px;
  }
  .auth-demo-player {
    background: #000; border: none; border-radius: 10px;
    overflow: hidden; position: relative; width: 100%;
    max-width: 860px; margin: 0 auto;
    aspect-ratio: 16 / 9;
  }
  .auth-demo-player iframe {
    position: absolute; inset: 0;
    width: 100%; height: 100%; border: none; border-radius: 10px; display: block;
  }
  .auth-demo-caption {
    font-size: 11px; color: var(--text3); margin-top: 10px; text-align: center;
  }

  .auth-pricing { display: flex; gap: 14px; margin-bottom: 36px; }
  .auth-price-card {
    flex: 1; background: var(--surface); border: 1px solid var(--border);
    border-radius: 12px; padding: 18px; position: relative;
  }
  .auth-price-card.featured { border-color: var(--accent); }
  .auth-price-badge {
    position: absolute; top: -9px; right: 14px;
    background: var(--accent); color: #000; font-size: 9px; font-weight: 700;
    padding: 3px 8px; border-radius: 4px; text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  .auth-price-name { font-size: 14px; font-weight: 600; color: var(--text); margin-bottom: 4px; }
  .auth-price-amount {
    font-family: 'Fraunces', serif; font-size: 28px; font-weight: 500; color: var(--text);
  }
  .auth-price-amount span { font-size: 13px; font-weight: 400; color: var(--text2); }
  .auth-price-list { list-style: none; padding: 0; margin: 12px 0 0; }
  .auth-price-list li {
    font-size: 12px; color: var(--text2); padding: 3px 0;
    display: flex; align-items: center; gap: 6px;
  }
  .auth-price-list li::before { content: "✓"; color: var(--accent); font-size: 11px; }
  .auth-price-trial { font-size: 11px; color: var(--text3); margin-top: 10px; }
  .auth-price-cta {
    width: 100%; padding: 14px; border: none; border-radius: 8px;
    font-size: 14px; font-weight: 600; cursor: pointer;
    font-family: 'Geist', sans-serif; margin-top: 14px;
    transition: opacity 0.15s, transform 0.15s;
  }
  .auth-price-cta:hover { opacity: 0.9; transform: translateY(-1px); }
  .auth-price-cta.starter { background: var(--accent); color: #0d0d0d; }
  .auth-price-cta.pro { background: #fff; color: #0f0e0c; }
  .auth-price-sub { font-size: 10px; color: var(--text3); margin-top: 6px; text-align: center; }
  .auth-plan-hint {
    text-align: center; font-size: 12px; color: var(--text3); margin-top: -16px; margin-bottom: 36px;
  }
  .auth-plan-banner {
    background: var(--accent); color: #0f0e0c; border-radius: 6px;
    padding: 10px 14px; margin-bottom: 16px; font-size: 13px; font-weight: 500;
    text-align: center;
  }

  .auth-support {
    border-top: 1px solid var(--border); padding-top: 24px; margin-top: 8px;
  }
  .auth-support-title { font-size: 12px; color: var(--text3); margin-bottom: 6px; }
  .auth-support-email { color: var(--accent); font-size: 13px; text-decoration: none; }
  .auth-support-email:hover { text-decoration: underline; }
  .auth-support-note { font-size: 11px; color: var(--text3); margin-top: 4px; }

  .auth-card {
    background: var(--surface); border: 1px solid var(--border); border-radius: 16px;
    padding: 32px; width: 100%; max-width: 400px;
  }
  .auth-tabs { display: flex; gap: 0; margin-bottom: 24px; border-bottom: 1px solid var(--border); }
  .auth-tab {
    flex: 1; padding: 10px; text-align: center;
    font-size: 13px; font-weight: 500; color: var(--text2);
    background: transparent; border: none; cursor: pointer;
    font-family: 'Geist', sans-serif; border-bottom: 2px solid transparent;
    transition: all 0.15s;
  }
  .auth-tab.active { color: var(--accent); border-bottom-color: var(--accent); }
  .auth-field { display: flex; flex-direction: column; gap: 5px; margin-bottom: 16px; }
  .auth-field label { font-size: 11px; font-weight: 500; color: var(--text2); letter-spacing: 0.3px; }
  .auth-btn {
    width: 100%; padding: 12px; border-radius: 8px; border: none;
    background: var(--accent); color: #000; font-weight: 600;
    font-size: 14px; cursor: pointer; font-family: 'Geist', sans-serif;
    transition: opacity 0.15s;
  }
  .auth-btn:hover { opacity: 0.9; }
  .auth-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .auth-fine { font-size: 11px; color: var(--text3); text-align: center; margin-top: 14px; line-height: 1.5; }
  .auth-forgot {
    font-size: 12px; color: var(--text3); background: none; border: none;
    cursor: pointer; font-family: 'Geist', sans-serif; padding: 0;
    margin-top: 12px; transition: color 0.15s; display: block; text-align: center; width: 100%;
  }
  .auth-forgot:hover { color: var(--accent); }

  .auth-back {
    display: flex; align-items: center; gap: 6px;
    font-size: 13px; color: var(--text2); background: none; border: none;
    cursor: pointer; font-family: 'Geist', sans-serif; padding: 0;
    margin-bottom: 20px; transition: color 0.15s;
  }
  .auth-back:hover { color: var(--text); }
  .auth-card-title {
    font-family: 'Fraunces', serif; font-size: 22px; font-weight: 500;
    color: var(--text); margin-bottom: 6px;
  }
  .auth-card-sub {
    font-size: 13px; color: var(--text2); line-height: 1.5; margin-bottom: 20px;
  }
  .auth-hint { font-size: 12px; color: var(--text3); margin-bottom: 16px; }
  .auth-check-icon {
    width: 56px; height: 56px; border-radius: 50%;
    background: var(--accent-dim); color: var(--accent);
    display: flex; align-items: center; justify-content: center;
    font-size: 26px; margin: 0 auto 16px;
  }
  .auth-check-email {
    font-size: 13px; color: var(--text); font-weight: 500;
  }
  .auth-spam-note {
    font-size: 11px; color: var(--text3); margin-top: 16px; text-align: center;
  }
  .auth-back-link {
    font-size: 13px; color: var(--accent); background: none; border: none;
    cursor: pointer; font-family: 'Geist', sans-serif; padding: 0;
    margin-top: 20px; display: block; text-align: center; width: 100%;
    transition: opacity 0.15s;
  }
  .auth-back-link:hover { opacity: 0.8; }

  /* Reset Password Page */
  .reset-page {
    min-height: 100vh; background: var(--bg);
    display: flex; flex-direction: column; align-items: center; justify-content: center;
  }
  .reset-logo {
    position: absolute; top: 24px; left: 32px;
    font-family: 'Fraunces', serif; font-size: 20px; font-weight: 500;
    color: var(--text);
  }
  .reset-logo em { color: var(--accent); font-style: italic; }
  .auth-error {
    background: var(--red-dim); color: var(--red); padding: 10px 14px;
    border-radius: 8px; font-size: 13px; margin-bottom: 16px;
  }
  .auth-success {
    background: var(--green-dim); color: var(--green); padding: 10px 14px;
    border-radius: 8px; font-size: 13px; margin-bottom: 16px;
  }

  /* ─── HAMBURGER MENU ─── */
  .hamburger-btn {
    position: fixed; top: 18px; left: 18px; z-index: 1001;
    background: none; border: 1px solid var(--border); border-radius: 8px;
    color: var(--text); font-size: 22px; width: 40px; height: 40px;
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; font-family: 'Geist', sans-serif;
    transition: background 0.15s;
  }
  .hamburger-btn:hover { background: var(--surface2); }

  .hmenu-overlay {
    position: fixed; inset: 0; background: rgba(0,0,0,0.6);
    z-index: 1002; opacity: 0; pointer-events: none;
    transition: opacity 0.3s ease;
  }
  .hmenu-overlay.open { opacity: 1; pointer-events: auto; }

  .hmenu-panel {
    position: fixed; top: 0; left: 0; right: 0; z-index: 1003;
    background: #0f0e0c; border-bottom: 1px solid var(--border);
    transform: translateY(-100%); transition: transform 0.3s var(--ease-out-soft);
    max-height: 90vh; overflow-y: auto;
  }
  .hmenu-panel.open { transform: translateY(0); }

  .hmenu-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 18px 24px; border-bottom: 1px solid var(--border);
  }
  .hmenu-logo {
    font-family: 'Fraunces', serif; font-size: 20px; font-weight: 500;
    color: var(--text);
  }
  .hmenu-logo em { color: var(--accent); font-style: italic; }
  .hmenu-close {
    background: none; border: none; color: var(--text2); font-size: 24px;
    cursor: pointer; width: 36px; height: 36px;
    display: flex; align-items: center; justify-content: center;
    border-radius: 8px; transition: background 0.15s;
  }
  .hmenu-close:hover { background: var(--surface2); color: var(--text); }

  .hmenu-tabs {
    display: flex; gap: 0; border-bottom: 1px solid var(--border);
    padding: 0 24px;
  }
  .hmenu-tab {
    background: none; border: none; border-bottom: 2px solid transparent;
    color: var(--text2); font-size: 13px; font-weight: 500;
    padding: 14px 20px; cursor: pointer; font-family: 'Geist', sans-serif;
    transition: all 0.15s;
  }
  .hmenu-tab:hover { color: var(--text); }
  .hmenu-tab.active { color: var(--accent); border-bottom-color: var(--accent); }

  /* "Why Pitcherly" tab — mobile only */
  @media (min-width: 769px) {
    .mobile-only-tab { display: none !important; }
  }

  /* Why Pitcherly content */
  .why-features { display: flex; flex-direction: column; gap: 14px; margin: 20px 0 28px; }
  .why-feature {
    display: flex; align-items: flex-start; gap: 12px;
    padding: 12px; background: var(--surface2);
    border: 1px solid var(--border); border-radius: 10px;
  }
  .why-feature-icon { font-size: 22px; flex-shrink: 0; line-height: 1; }
  .why-feature-title { font-size: 14px; font-weight: 600; color: var(--text); margin-bottom: 2px; }
  .why-feature-desc { font-size: 12px; color: var(--text2); line-height: 1.4; }
  .why-section-label {
    font-size: 11px; font-weight: 600; text-transform: uppercase;
    letter-spacing: 1.2px; color: var(--text3);
    margin: 24px 0 12px;
  }
  .why-demo .auth-demo-player { max-width: 720px; }
  .why-pricing { display: flex; flex-direction: column; gap: 14px; }
  .why-price-card {
    background: var(--surface2); border: 1px solid var(--border);
    border-radius: 12px; padding: 20px; position: relative;
  }
  .why-price-card.featured { border-color: var(--accent); }
  .why-price-badge {
    position: absolute; top: -10px; right: 16px;
    background: var(--accent); color: #000; font-size: 9px; font-weight: 700;
    padding: 4px 10px; border-radius: 4px; text-transform: uppercase; letter-spacing: 0.5px;
  }
  .why-price-name { font-size: 14px; font-weight: 600; color: var(--text); margin-bottom: 4px; }
  .why-price-amount {
    font-family: 'Fraunces', serif; font-size: 30px; font-weight: 500; color: var(--text);
  }
  .why-price-amount span { font-size: 13px; font-weight: 400; color: var(--text2); }
  .why-price-list { list-style: none; padding: 0; margin: 14px 0 18px; }
  .why-price-list li {
    font-size: 13px; color: var(--text2); padding: 4px 0;
    display: flex; align-items: center; gap: 8px;
  }
  .why-price-list li::before { content: "✓"; color: var(--accent); font-size: 12px; }
  .why-price-cta {
    width: 100%; padding: 14px; min-height: 52px; border: none; border-radius: 8px;
    font-size: 15px; font-weight: 600; cursor: pointer;
    font-family: 'Geist', sans-serif;
  }
  .why-price-cta.starter { background: var(--accent); color: #0d0d0d; }
  .why-price-cta.pro { background: #fff; color: #0f0e0c; }
  .why-fine { text-align: center; font-size: 11px; color: var(--text3); margin-top: 18px; }

  .hmenu-body { padding: 32px 24px; max-width: 900px; margin: 0 auto; }

  .hmenu-heading {
    font-family: 'Fraunces', serif; font-size: 24px; font-weight: 500;
    color: var(--text); margin-bottom: 16px; letter-spacing: -0.3px;
  }
  .hmenu-text {
    font-size: 14px; color: var(--text2); line-height: 1.7; margin-bottom: 14px;
    max-width: 700px;
  }
  .hmenu-founded {
    font-size: 12px; color: var(--text3); margin-bottom: 24px;
    text-transform: uppercase; letter-spacing: 1px;
  }
  .hmenu-stats {
    display: flex; gap: 16px; flex-wrap: wrap;
  }
  .hmenu-stat {
    flex: 1; min-width: 140px; background: var(--surface);
    border: 1px solid var(--border); border-radius: 10px;
    padding: 20px; text-align: center;
  }
  .hmenu-stat-val {
    font-family: 'Fraunces', serif; font-size: 22px; font-weight: 500;
    color: var(--accent); margin-bottom: 4px;
  }
  .hmenu-stat-label { font-size: 12px; color: var(--text2); }

  .hmenu-contact-info { margin-bottom: 24px; }
  .hmenu-contact-info a {
    color: var(--accent); text-decoration: none; font-size: 14px;
  }
  .hmenu-contact-info a:hover { text-decoration: underline; }
  .hmenu-response {
    font-size: 12px; color: var(--text3); margin-top: 6px;
  }

  .hmenu-form { display: flex; flex-direction: column; gap: 12px; max-width: 500px; }
  .hmenu-form input, .hmenu-form textarea {
    width: 100%; padding: 10px 14px; background: var(--surface2);
    border: 1px solid var(--border); border-radius: 8px;
    color: var(--text); font-size: 14px; font-family: 'Geist', sans-serif;
    outline: none; transition: border-color 0.15s;
  }
  .hmenu-form input:focus, .hmenu-form textarea:focus {
    border-color: var(--accent);
  }
  .hmenu-form textarea { resize: vertical; min-height: 100px; }
  .hmenu-form-btn {
    align-self: flex-start; padding: 10px 24px; background: var(--accent);
    color: #000; border: none; border-radius: 8px; font-size: 13px;
    font-weight: 600; cursor: pointer; font-family: 'Geist', sans-serif;
    transition: background 0.15s;
  }
  .hmenu-form-btn:hover { background: var(--accent2); }
  .hmenu-form-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .hmenu-form-success {
    background: var(--green-dim); color: var(--green); padding: 10px 14px;
    border-radius: 8px; font-size: 13px;
  }

  .hmenu-subtitle {
    font-size: 14px; color: var(--text2); margin-bottom: 20px; line-height: 1.5;
  }
  .hmenu-bullets {
    list-style: none; padding: 0; margin: 20px 0 24px;
    display: flex; flex-direction: column; gap: 8px;
  }
  .hmenu-bullets li {
    font-size: 13px; color: var(--text2); display: flex; align-items: center; gap: 8px;
  }
  .hmenu-bullets li span { color: var(--green); }
  .hmenu-cta {
    display: inline-block; padding: 12px 28px; background: var(--accent);
    color: #000; border: none; border-radius: 8px; font-size: 14px;
    font-weight: 600; cursor: pointer; font-family: 'Geist', sans-serif;
    text-decoration: none; transition: background 0.15s;
  }
  .hmenu-cta:hover { background: var(--accent2); }
  .hmenu-placeholder {
    background: var(--surface); border: 1px solid var(--border);
    border-radius: 10px; padding: 60px 20px; text-align: center;
    color: var(--text3); font-size: 14px;
  }

  .hmenu-features-grid {
    display: grid; grid-template-columns: repeat(3, 1fr);
    gap: 14px; margin-bottom: 36px;
  }
  .hmenu-feature-card {
    background: var(--surface); border: 1px solid var(--border);
    border-radius: 10px; padding: 20px;
  }
  .hmenu-feature-icon { font-size: 20px; margin-bottom: 8px; }
  .hmenu-feature-name {
    font-size: 14px; font-weight: 600; color: var(--text); margin-bottom: 4px;
  }
  .hmenu-feature-desc { font-size: 12px; color: var(--text2); line-height: 1.4; }

  .hmenu-compare-wrap { width: 100%; }
  .hmenu-compare {
    width: 100%; border-collapse: collapse; font-size: 13px;
  }
  .hmenu-compare th {
    text-align: left; padding: 10px 14px; font-size: 11px;
    text-transform: uppercase; letter-spacing: 0.5px;
    color: var(--text3); font-weight: 600;
    border-bottom: 1px solid var(--border);
  }
  .hmenu-compare td {
    padding: 10px 14px; border-bottom: 1px solid var(--border);
    color: var(--text2);
  }
  .hmenu-compare td:first-child { color: var(--text); font-weight: 500; }
  .hmenu-compare th.hmenu-pro-col, .hmenu-compare td.hmenu-pro-col {
    background: rgba(212,145,92,0.04);
    border-left: 1px solid rgba(212,145,92,0.2);
    border-right: 1px solid rgba(212,145,92,0.2);
  }
  .hmenu-compare th.hmenu-pro-col { color: var(--accent); }
  .hmenu-check { color: var(--green); }
  .hmenu-x { color: var(--text3); }

  /* ─── ACCOUNT PAGE ─── */
  .acct-profile-card {
    background: var(--surface); border: 1px solid var(--border); border-radius: 12px;
    padding: 24px; display: flex; flex-direction: column; align-items: center; gap: 14px;
  }
  .acct-avatar {
    width: 72px; height: 72px; border-radius: 50%;
    background: var(--accent-dim); color: var(--accent);
    display: flex; align-items: center; justify-content: center;
    font-family: 'Fraunces', serif; font-size: 26px; font-weight: 600;
    letter-spacing: -0.5px;
  }
  .acct-name-input {
    text-align: center; font-size: 16px; font-weight: 500; color: var(--text);
    background: transparent; border: none; border-bottom: 1px solid transparent;
    padding: 4px 0; font-family: 'Geist', sans-serif; outline: none;
    width: 100%; max-width: 220px; transition: border-color 0.15s;
  }
  .acct-name-input:focus { border-bottom-color: var(--accent); }
  .acct-email { font-size: 12px; color: var(--text3); margin-top: -6px; }
  .acct-plan-pill {
    padding: 3px 10px; border-radius: 12px;
    font-size: 10px; font-weight: 600; text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  .acct-plan-pill.pro { background: var(--accent-dim); color: var(--accent); }
  .acct-plan-pill.starter { background: var(--surface2); color: var(--text3); }
  .acct-save-btn {
    width: 100%; max-width: 220px; padding: 8px; border-radius: 8px; border: none;
    background: var(--accent); color: #000; font-weight: 600;
    font-size: 12px; cursor: pointer; font-family: 'Geist', sans-serif;
    transition: opacity 0.15s;
  }
  .acct-save-btn:hover { opacity: 0.9; }
  .acct-save-btn.saved { background: var(--green); }

  .acct-cards { display: flex; flex-direction: column; gap: 16px; }

  .acct-btn {
    width: 100%; padding: 10px; border-radius: 8px; border: 1px solid var(--border);
    font-size: 13px; font-weight: 500; cursor: pointer;
    font-family: 'Geist', sans-serif; transition: all 0.15s;
    background: transparent; color: var(--text);
  }
  .acct-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .acct-btn.accent { background: var(--accent); color: #000; border-color: var(--accent); }
  .acct-btn.accent:hover { opacity: 0.9; }
  .acct-btn.upgrade {
    background: var(--accent); color: #000; border-color: var(--accent);
    padding: 13px; font-size: 14px; font-weight: 600;
  }
  .acct-btn.upgrade:hover { opacity: 0.9; }
  .acct-btn.logout {
    background: var(--surface2); color: var(--text); border: 1px solid var(--border);
  }
  .acct-btn.logout:hover { background: var(--border); }

  .acct-upgrade-sub { font-size: 11px; color: var(--text2); text-align: center; margin-top: -8px; }

  .status-dot-green {
    width: 7px; height: 7px; border-radius: 50%; background: var(--green);
    display: inline-block; box-shadow: 0 0 6px var(--green);
  }

  .cancel-link {
    font-size: 12px; color: var(--text3); background: none; border: none;
    cursor: pointer; font-family: 'Geist', sans-serif; padding: 0;
    transition: color 0.15s; text-align: left;
  }
  .cancel-link:hover { color: var(--red); }

  .cancel-confirm {
    background: var(--red-dim); border: 1px solid rgba(224,92,92,0.2);
    border-radius: 8px; padding: 12px; font-size: 12px;
    color: var(--text2); line-height: 1.5;
  }
  .cancel-confirm-actions { display: flex; gap: 8px; margin-top: 10px; }
  .cancel-confirm-btn {
    padding: 6px 16px; border-radius: 6px; font-size: 12px;
    font-family: 'Geist', sans-serif; cursor: pointer;
    border: 1px solid var(--border); transition: all 0.15s;
  }
  .cancel-confirm-btn.yes { background: var(--red); color: #fff; border-color: var(--red); }
  .cancel-confirm-btn.yes:hover { opacity: 0.9; }
  .cancel-confirm-btn.no { background: transparent; color: var(--text2); }
  .cancel-confirm-btn.no:hover { background: var(--surface2); }

  .usage-ring-wrap { display: flex; align-items: center; gap: 20px; padding: 4px 0; }
  .usage-ring { width: 64px; height: 64px; flex-shrink: 0; position: relative; }
  .usage-ring svg { transform: rotate(-90deg); }
  .usage-ring-bg { fill: none; stroke: var(--surface2); stroke-width: 5; }
  .usage-ring-fill { fill: none; stroke-width: 5; stroke-linecap: round; transition: stroke-dashoffset 0.5s ease; }
  .usage-ring-fill.ok { stroke: var(--accent); }
  .usage-ring-fill.low { stroke: var(--red); }
  .usage-ring-text {
    position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
    font-size: 14px; font-weight: 600; color: var(--text);
  }
  .usage-ring-info { font-size: 13px; color: var(--text2); line-height: 1.5; }
  .usage-ring-info strong { color: var(--text); font-weight: 600; }

  .acct-msg { padding: 8px 12px; border-radius: 8px; font-size: 12px; }
  .acct-msg.success { background: var(--green-dim); color: var(--green); }
  .acct-msg.error { background: var(--red-dim); color: var(--red); }

  .danger-card {
    background: var(--surface); border: 1px solid rgba(224,92,92,0.25); border-radius: 12px;
    overflow: hidden;
  }
  .danger-card .section-head { color: var(--red); }
  .danger-card .section-body { padding: 16px; }

  /* Sidebar */
  .unlimited-badge {
    display: flex; align-items: center; justify-content: center; gap: 4px;
    padding: 8px 12px; border-radius: 8px;
    background: var(--accent-dim); color: var(--accent);
    font-size: 12px; font-weight: 600; letter-spacing: 0.3px;
  }
  .sidebar-account-divider {
    margin: 8px 12px 0; border: none; border-top: 1px solid var(--border);
  }
  .sidebar-account-tab {
    display: flex; align-items: center; gap: 10px;
    padding: 9px 12px; margin: 6px 12px 0; border-radius: 8px;
    font-size: 13px; font-weight: 400; color: var(--text3);
    cursor: pointer; border: none; background: transparent;
    font-family: 'Geist', sans-serif; transition: all 0.15s;
    text-align: left; width: calc(100% - 24px);
  }
  .sidebar-account-tab:hover { color: var(--text2); background: var(--surface2); }
  .sidebar-account-tab.active { background: var(--accent-dim); color: var(--accent); font-weight: 500; }

  /* ─── COMING SOON PAGE ─── */
  .coming-page {
    padding: 40px 48px; max-width: 960px; margin: 0 auto;
  }
  .coming-header {
    margin-bottom: 32px;
  }
  .coming-title {
    font-family: 'Fraunces', serif; font-size: 28px; font-weight: 500;
    color: var(--text); margin-bottom: 8px;
  }
  .coming-sub {
    font-size: 14px; color: var(--text2); line-height: 1.5;
  }
  .coming-grid {
    display: grid; grid-template-columns: 1fr 1fr; gap: 16px;
    margin-bottom: 40px;
  }
  @media (max-width: 768px) { .coming-grid { grid-template-columns: 1fr; } .coming-page { padding: 24px 16px; } }
  .coming-card {
    background: var(--surface); border: 1px solid var(--border); border-radius: 12px;
    padding: 20px; display: flex; flex-direction: column; gap: 10px;
    transition: border-color 0.15s;
  }
  .coming-card:hover { border-color: var(--border2); }
  .coming-card-top {
    display: flex; align-items: center; justify-content: space-between; gap: 8px;
  }
  .coming-card-name {
    font-size: 15px; font-weight: 600; color: var(--text);
  }
  .coming-badge {
    font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px;
    padding: 3px 8px; border-radius: 10px; white-space: nowrap;
  }
  .coming-badge.accent { background: var(--accent-dim); color: var(--accent); }
  .coming-badge.blue { background: var(--blue-dim); color: var(--blue); }
  .coming-badge.green { background: var(--green-dim); color: var(--green); }
  .coming-badge.muted { background: var(--surface2); color: var(--text3); }
  .coming-card-desc {
    font-size: 13px; color: var(--text2); line-height: 1.5;
  }
  .coming-vote {
    display: flex; align-items: center; gap: 6px; margin-top: 4px;
    background: none; border: 1px solid var(--border); border-radius: 8px;
    padding: 5px 10px; cursor: pointer; font-family: 'Geist', sans-serif;
    font-size: 12px; color: var(--text3); transition: all 0.15s;
    align-self: flex-start;
  }
  .coming-vote:hover { border-color: var(--accent); color: var(--text2); }
  .coming-vote.voted { border-color: var(--accent); color: var(--accent); background: var(--accent-dim); }
  .coming-request {
    background: var(--surface); border: 1px solid var(--border); border-radius: 12px;
    padding: 24px;
  }
  .coming-request-title {
    font-size: 16px; font-weight: 600; color: var(--text); margin-bottom: 4px;
  }
  .coming-request-sub {
    font-size: 13px; color: var(--text2); margin-bottom: 16px;
  }
  .coming-request-row {
    display: flex; gap: 8px;
  }
  .coming-request-row input { flex: 1; }
  .coming-request-btn {
    padding: 8px 16px; border-radius: 8px; border: none;
    background: var(--accent); color: #0d0d0d; font-family: 'Geist', sans-serif;
    font-size: 13px; font-weight: 500; cursor: pointer; white-space: nowrap;
    transition: opacity 0.15s;
  }
  .coming-request-btn:hover { opacity: 0.85; }
  .coming-request-btn:disabled { opacity: 0.5; cursor: not-allowed; }

  /* ─── LEGAL PAGES ─── */
  .legal-page {
    min-height: 100vh; background: var(--bg); padding: 40px 24px 60px;
    display: flex; flex-direction: column; align-items: center;
  }
  .legal-back {
    align-self: flex-start; max-width: 720px; width: 100%; margin: 0 auto 24px;
    background: none; border: none; color: var(--text2); font-size: 13px;
    cursor: pointer; font-family: 'Geist', sans-serif; padding: 0;
    transition: color 0.15s;
  }
  .legal-back:hover { color: var(--text); }
  .legal-content {
    max-width: 720px; width: 100%;
  }
  .legal-title {
    font-family: 'Fraunces', serif; font-size: 28px; font-weight: 500;
    color: var(--text); margin-bottom: 8px; letter-spacing: -0.3px;
  }
  .legal-updated {
    font-size: 12px; color: var(--text3); margin-bottom: 32px;
  }
  .legal-section {
    margin-bottom: 24px;
  }
  .legal-section-title {
    font-size: 14px; font-weight: 600; color: var(--text); margin-bottom: 6px;
  }
  .legal-section-body {
    font-size: 13px; color: var(--text2); line-height: 1.75;
  }
  .legal-section-body a {
    color: var(--accent); text-decoration: none;
  }
  .legal-section-body a:hover { text-decoration: underline; }

  .auth-agree {
    display: flex; align-items: flex-start; gap: 8px;
    font-size: 12px; color: var(--text2); margin-bottom: 4px; margin-top: -4px;
  }
  .auth-agree input[type="checkbox"] {
    margin-top: 2px; accent-color: var(--accent); cursor: pointer;
  }
  .auth-agree a {
    color: var(--accent); text-decoration: none;
  }
  .auth-agree a:hover { text-decoration: underline; }

  /* ─── LANDSCAPE PHONES ─── */
  @media (max-width: 932px) and (orientation: landscape) and (max-height: 500px) {
    .auth-right { padding: 24px 16px; min-height: auto; }
    .auth-card { padding: 18px; }
    .auth-hero { font-size: 22px; }
    .layout { padding: 12px; gap: 12px; }
    .paywall { padding: 32px 16px; }
    .paywall-headline { font-size: 22px; }
  }

  /* ─── MOTION: press feedback + entrances (all auto-disabled by the reduced-motion block below) ─── */
  .gen-btn:active:not(:disabled), .act-btn:active, .upgrade-btn:active, .apply-btn:active,
  .plan-cta:active, .auth-btn:active:not(:disabled), .acct-btn:active:not(:disabled),
  .acct-save-btn:active:not(:disabled), .hmenu-cta:active, .hmenu-form-btn:active:not(:disabled),
  .pill:active, .improve-btn:active, .update-btn:active, .coming-vote:active,
  .history-item:active, .otab:active, .sidebar-tab:active, .auth-price-cta:active,
  .why-price-cta:active, .lp-nav-btn:active {
    transform: scale(0.97);
  }

  /* Recent Outputs — stagger the rows in on mount */
  @keyframes fadeInUp { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
  .history-list .history-item { animation: fadeInUp 0.4s var(--ease-out-soft) both; }
  .history-list .history-item:nth-child(1) { animation-delay: 0ms; }
  .history-list .history-item:nth-child(2) { animation-delay: 45ms; }
  .history-list .history-item:nth-child(3) { animation-delay: 90ms; }
  .history-list .history-item:nth-child(4) { animation-delay: 135ms; }
  .history-list .history-item:nth-child(5) { animation-delay: 180ms; }
  .history-list .history-item:nth-child(n+6) { animation-delay: 220ms; }

  /* Generation output box — gentle entrance on each result/tab change (the per-tab .outbox is not
     wrapped in .result, so it had no entrance). Plays once per mount. */
  .output-panel .outbox { animation: fadeUp 0.4s var(--ease-out-soft) both; }

  /* Modal entrance — backdrop fade + card scale/translate in */
  @keyframes modalBackdropIn { from { opacity: 0; } to { opacity: 1; } }
  @keyframes modalCardIn { from { opacity: 0; transform: translateY(8px) scale(0.97); } to { opacity: 1; transform: translateY(0) scale(1); } }
  .modal-backdrop-anim { animation: modalBackdropIn 0.2s ease both; }
  .modal-card-anim { animation: modalCardIn 0.28s var(--ease-out-soft) both; }

  /* ─── LANDING HERO · "STRATA" (Direction C) ─── */
  .lp-hero { position: relative; overflow: hidden; }
  .lp-hero-content { position: relative; z-index: 2; }
  /* Cursor-follow glow: a fixed-size element MOVED by a compositor-only transform (+ opacity),
     never by re-centering the gradient — so it never triggers a per-frame repaint. */
  .lp-hero-glow {
    position: absolute; top: 0; left: 0; width: 480px; height: 480px; margin: -240px 0 0 -240px;
    z-index: 0; pointer-events: none; opacity: var(--glow-o, 0);
    background: radial-gradient(240px circle at center, rgba(212,145,92,0.16), rgba(212,145,92,0.06) 40%, transparent 68%);
    transform: translate3d(var(--glow-x, 0px), var(--glow-y, 0px), 0);
    will-change: transform, opacity; transition: opacity 0.35s ease;
  }
  /* Centered "stage" so the cards frame the headline at any width instead of flying to the
     far screen corners on wide monitors (which left the right side looking empty). */
  .lp-hero-cards { position: absolute; top: 0; bottom: 0; left: 50%; transform: translateX(-50%); width: 100%; max-width: 1320px; z-index: 1; pointer-events: none; }
  .lp-card {
    position: absolute; width: 244px; box-sizing: border-box;
    background: linear-gradient(180deg, rgba(31,31,31,0.94), rgba(20,20,20,0.94));
    border: 1px solid var(--border); border-radius: 14px; padding: 13px 14px;
    box-shadow: 0 22px 55px rgba(0,0,0,0.5), 0 0 34px rgba(212,145,92,0.10);
    opacity: 0.4; pointer-events: auto;
    transform: translate3d(calc(var(--par-x, 0) * var(--f, 6px)), calc(var(--par-y, 0) * var(--f, 6px) + var(--lift, 0px)), 0) rotate(var(--rot, 0deg));
    transition: transform 0.5s var(--ease-out-soft), opacity 0.3s ease, box-shadow 0.3s ease;
  }
  .lp-card-1 { left: 0;   top: 26%;   --f: 6px;  --rot: -4deg; }  /* CMA — anchors the left, mid-height */
  .lp-card-2 { right: 0;  top: 3%;    --f: 9px;  --rot: 3deg; }   /* MLS — upper-right */
  .lp-card-3 { right: 5%; bottom: 4%; --f: 11px; --rot: -2deg; }  /* Property Info — lower-right */
  .lp-card:hover { opacity: 0.66; --lift: -8px; box-shadow: 0 30px 65px rgba(0,0,0,0.55), 0 0 46px rgba(212,145,92,0.20); }
  .lp-hero-cards:has(.lp-card:hover) .lp-card:not(:hover) { opacity: 0.24; }
  .lp-card-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 9px; }
  .lp-card-tag { font-size: 9px; font-weight: 700; letter-spacing: 1.2px; color: var(--accent); text-transform: uppercase; }
  .lp-card-pill { font-size: 8px; font-weight: 700; letter-spacing: 0.6px; color: var(--accent); background: var(--accent-dim); border: 1px solid rgba(212,145,92,0.3); border-radius: 999px; padding: 2px 6px; text-transform: uppercase; }
  .lp-card-statusdot { width: 6px; height: 6px; border-radius: 50%; background: var(--green); box-shadow: 0 0 6px var(--green); }
  .lp-card-title { font-size: 11px; font-weight: 600; color: var(--text2); margin-bottom: 10px; }
  .lp-card-bar { display: block; width: 100%; height: auto; margin-bottom: 9px; }
  .lp-comp-row { display: flex; align-items: center; gap: 7px; margin-top: 7px; }
  .lp-comp-dot { width: 5px; height: 5px; border-radius: 50%; background: var(--accent); flex-shrink: 0; }
  .lp-comp-line { height: 6px; border-radius: 3px; background: var(--border2); flex: 1; }
  .lp-comp-line.short { flex: 0 0 44%; }
  .lp-comp-price { font-size: 10px; font-weight: 600; color: var(--text); font-variant-numeric: tabular-nums; flex-shrink: 0; }
  .lp-mls-line { height: 7px; border-radius: 3px; background: var(--border2); margin-bottom: 7px; }
  .lp-mls-line.w1 { width: 100%; } .lp-mls-line.w2 { width: 92%; } .lp-mls-line.w3 { width: 97%; } .lp-mls-line.w4 { width: 58%; }
  .lp-mls-copy { display: inline-flex; align-items: center; gap: 5px; margin-top: 10px; font-size: 10px; font-weight: 600; color: var(--green); border: 1px solid var(--green); background: var(--green-dim); border-radius: 6px; padding: 4px 9px; }
  .lp-info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 9px 12px; }
  .lp-info-cell { display: flex; flex-direction: column; gap: 3px; }
  .lp-info-label { font-size: 8px; font-weight: 600; letter-spacing: 0.4px; color: var(--text3); text-transform: uppercase; }
  .lp-info-value { font-size: 15px; font-weight: 600; color: var(--text); font-family: 'Fraunces', serif; line-height: 1; }

  /* Social-proof marquee */
  .lp-marquee { position: relative; overflow: hidden; max-width: 1100px; margin: 4px auto 0; padding: 18px 0 4px;
    -webkit-mask-image: linear-gradient(90deg, transparent, #000 12%, #000 88%, transparent);
    mask-image: linear-gradient(90deg, transparent, #000 12%, #000 88%, transparent); }
  .lp-marquee-track { display: inline-flex; align-items: center; white-space: nowrap; will-change: transform; animation: lpMarquee 42s linear infinite; }
  .lp-marquee:hover .lp-marquee-track { animation-play-state: paused; }
  @keyframes lpMarquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
  .lp-marquee-item { display: inline-flex; align-items: center; font-size: 12.5px; font-weight: 500; color: var(--text3); padding: 0 4px; }
  .lp-marquee-sep { color: var(--accent); opacity: 0.55; padding: 0 18px; }

  /* Mobile: cards become a static stack below the CTAs; no parallax/glow */
  @media (max-width: 768px) {
    .lp-hero { overflow: visible; }
    .lp-hero-glow { display: none; }
    .lp-hero-cards { position: static; inset: auto; left: auto; transform: none; max-width: none; display: flex; flex-direction: column; gap: 12px; padding: 4px 20px 0; pointer-events: none; }
    .lp-card { position: static; left: auto; right: auto; top: auto; bottom: auto; width: 100%; opacity: 1; --rot: 0deg; --f: 0px; --lift: 0px; transform: none; box-shadow: 0 14px 34px rgba(0,0,0,0.42), 0 0 0 1px rgba(255,255,255,0.02); }
    .lp-marquee-track { animation-duration: 32s; }
  }

  /* ═══════ MOBILE UX PASS (Direction C) — touch targets · safe-area · tactile · sheets ═══════ */
  @media (prefers-reduced-motion: no-preference) { html { scroll-behavior: smooth; } }

  /* Fair Housing compliance banner */
  .fh-banner { border: 1px solid var(--accent); background: var(--accent-dim); border-radius: 10px; margin-bottom: 14px; overflow: hidden; animation: fadeUp 0.25s var(--ease-out-soft) both; }
  .fh-banner-head { width: 100%; display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 11px 14px; background: none; border: none; cursor: pointer; color: var(--text); font-family: 'Geist', sans-serif; }
  .fh-banner-title { font-size: 13px; font-weight: 600; color: var(--accent); text-align: left; }
  .fh-banner-toggle { font-size: 12px; color: var(--text2); flex-shrink: 0; }
  .fh-banner-body { padding: 2px 14px 12px; }
  .fh-flag { display: flex; gap: 10px; padding: 9px 0; border-top: 1px solid var(--border); }
  .fh-sev { flex-shrink: 0; font-size: 10px; font-weight: 700; letter-spacing: 0.4px; text-transform: uppercase; padding: 3px 7px; border-radius: 5px; height: fit-content; }
  .fh-sev-block { background: var(--red-dim); color: var(--red); border: 1px solid var(--red); }
  .fh-sev-caution { background: rgba(212,145,92,0.14); color: var(--accent); border: 1px solid rgba(212,145,92,0.4); }
  .fh-flag-text { font-size: 13px; color: var(--text); line-height: 1.5; }
  .fh-phrase { font-weight: 600; }
  .fh-class { font-size: 11px; color: var(--text3); }
  .fh-sugg { font-size: 12px; color: var(--text2); margin-top: 3px; }
  .fh-banner-foot { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-top: 10px; flex-wrap: wrap; }
  .fh-disclaimer { font-size: 11px; color: var(--text3); }
  .fh-dismiss { font-size: 12px; color: var(--text2); background: none; border: 1px solid var(--border2); border-radius: 6px; padding: 6px 12px; cursor: pointer; min-height: 36px; }
  .fh-dismiss:active { transform: scale(0.97); }
  @media (max-width: 768px) { .fh-banner-head { min-height: 44px; } .fh-dismiss { min-height: 44px; } }

  /* MLS character counter (Listing output) */
  .mls-counter { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-top: 10px; padding-top: 10px; border-top: 1px solid var(--border); }
  .mls-board-select { font-size: 12px; padding: 6px 8px; background: var(--bg); border: 1px solid var(--border); border-radius: 6px; color: var(--text2); font-family: 'Geist', sans-serif; cursor: pointer; max-width: 220px; }
  .mls-count { font-size: 12px; font-weight: 600; font-variant-numeric: tabular-nums; }

  /* Multi-touch follow-up timeline (Day 1 / 3 / 7) */
  .followup-timeline { position: relative; }
  .followup-step { position: relative; display: flex; gap: 14px; padding-bottom: 14px; }
  .followup-rail { position: relative; flex-shrink: 0; width: 14px; display: flex; justify-content: center; }
  .followup-rail::before { content: ""; position: absolute; top: 8px; bottom: -6px; width: 2px; background: var(--border2); }
  .followup-step.last .followup-rail::before { display: none; }
  .followup-dot { position: relative; z-index: 1; width: 12px; height: 12px; border-radius: 50%; background: var(--accent); margin-top: 4px; box-shadow: 0 0 0 3px var(--bg); }
  .followup-step .email-card { flex: 1; min-width: 0; }
  .followup-day { font-size: 12px; font-weight: 700; color: var(--text); letter-spacing: 0.3px; }

  /* Scroll-triggered reveal utility (IntersectionObserver adds .is-revealed once) */
  .reveal-on-scroll { opacity: 0; transform: translateY(18px); transition: opacity 0.6s var(--ease-out-soft), transform 0.6s var(--ease-out-soft); }
  .reveal-on-scroll.is-revealed { opacity: 1; transform: none; }
  @media (prefers-reduced-motion: reduce) { .reveal-on-scroll { opacity: 1; transform: none; transition: none; } }

  /* Tab-switch fade (keyed remount on tab change re-fires) + signup-field fade-in */
  @keyframes fadeMain { from { opacity: 0; } to { opacity: 1; } }
  .main-content { animation: fadeMain 0.16s var(--ease-out-soft); }
  .auth-signup-anim { animation: fadeUp 0.22s var(--ease-out-soft) both; }

  /* Bottom-sheet drag handle (shown only when a modal becomes a sheet on mobile) */
  .sheet-handle { display: none; }
  @keyframes sheetUp { from { transform: translateY(100%); } to { transform: translateY(0); } }

  /* Tactile press feedback for buttons the global :active list missed (sub-100ms touch response) */
  .history-delete:active, .cancel-confirm-btn:active, .email-send-btn:active,
  .hamburger-btn:active, .picker-toggle:active, .bulk-btn:active,
  .lp-cta-primary:active, .lp-cta-ghost:active { transform: scale(0.97); }
  .contact-row-tap:active, .addr-suggest:active, .picker-row:active { background: var(--surface2); }

  @media (max-width: 768px) {
    /* Touch targets → 44px (WCAG 2.5.5) */
    .pill, .apply-btn, .act-btn, .improve-btn, .upgrade-btn, .cancel-confirm-btn { min-height: 44px; }
    .upgrade-btn { padding: 12px; font-size: 13px; }
    .cancel-confirm-btn { padding: 10px 16px; }
    .history-delete { min-width: 44px; min-height: 44px; margin-left: 4px; color: var(--red); }
    .picker-toggle { min-height: 44px !important; padding: 10px 12px !important; }
    .bulk-btn { min-height: 44px !important; padding: 8px 12px !important; font-size: 13px !important; }
    /* Billing-consequential skip-trial checkbox — was 14px (near-unhittable) */
    .plan-card input[type="checkbox"] { width: 20px !important; height: 20px !important; min-width: 20px; min-height: 20px; }

    /* Safe-area on the remaining fixed/full-screen surfaces (sidebar drawer already done) */
    .hamburger-btn { top: max(12px, env(safe-area-inset-top)); left: max(12px, env(safe-area-inset-left)); }
    .hmenu-panel { padding-top: env(safe-area-inset-top); }
    .hmenu-body { padding-bottom: max(24px, env(safe-area-inset-bottom)); }
    .paywall { padding-top: max(80px, calc(env(safe-area-inset-top) + 56px)); padding-left: max(16px, env(safe-area-inset-left)); padding-right: max(16px, env(safe-area-inset-right)); }

    /* Promoted Strata cards: legible text on the mobile stack (8-9px → floors) */
    .lp-card-tag { font-size: 11px; } .lp-card-pill { font-size: 10px; }
    .lp-info-label { font-size: 10px; } .lp-card-title { font-size: 12px; }

    /* Center modals → iOS bottom-sheet (opt-in via .sheet-overlay/.sheet-card) */
    .sheet-overlay { align-items: flex-end !important; padding: 0 !important; }
    .sheet-card {
      width: 100% !important; max-width: none !important; border-radius: 18px 18px 0 0 !important;
      max-height: 92dvh !important; padding-bottom: max(18px, env(safe-area-inset-bottom)) !important;
      animation: sheetUp 0.32s var(--ease-out-soft) both;
    }
    .sheet-handle { display: block; width: 36px; height: 4px; border-radius: 999px; background: var(--border2); margin: 10px auto 6px; flex-shrink: 0; }

    /* Thumb-zone Generate: a fixed bottom bar on mobile (opt-in via .sticky-cta). Both panels
       get bottom padding so the bar never covers the last form field or the output's Copy/Share. */
    .gen-btn.sticky-cta {
      position: fixed; left: 0; right: 0; bottom: 0; z-index: 40;
      width: 100%; border-radius: 0; margin: 0;
      padding-top: 15px; padding-bottom: max(15px, calc(env(safe-area-inset-bottom) + 6px));
      box-shadow: 0 -12px 28px rgba(13,13,13,0.65);
    }
    .layout:has(.gen-btn.sticky-cta) .input-panel,
    .layout:has(.gen-btn.sticky-cta) .output-panel { padding-bottom: 88px; }

    /* Drop the nested recipient-list scroll cap on mobile (single body scroll, no scroll-trap) */
    .batch-recipients { max-height: none !important; }
    /* Contact/property picker dropdowns → bottom-sheets on mobile */
    .picker-dropdown {
      position: fixed !important; left: 0 !important; right: 0 !important; bottom: 0 !important; top: auto !important;
      width: 100% !important; max-width: none !important; margin: 0 !important;
      border-radius: 18px 18px 0 0 !important; max-height: 72dvh; overflow-y: auto;
      padding-bottom: max(8px, env(safe-area-inset-bottom)) !important;
      box-shadow: 0 -14px 30px rgba(0,0,0,0.6) !important; z-index: 1000 !important;
      animation: sheetUp 0.3s var(--ease-out-soft) both;
    }
  }

  /* ─── REDUCED MOTION ─── */
  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
    }
    .sidebar { transition: none !important; }
    .lp-marquee-track { animation: none !important; transform: none !important; }
    .lp-card { transition: none !important; }
  }

  /* ─── HI-DPI ─── */
  @media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
    .sidebar { border-right-width: 0.5px; }
  }

  /* ─── PRINT ─── */
  @media print {
    .sidebar, .mobile-sidebar-toggle, .sidebar-backdrop, .hamburger-btn { display: none !important; }
    .main-content { margin-left: 0 !important; padding-top: 0 !important; }
    .layout { display: block !important; }
    button { display: none !important; }
  }

  /* ─── DVH FALLBACK ─── */
  @supports not (height: 100dvh) {
    .app { min-height: 100vh; }
  }

  /* ─── FOCUS RING ─── */
  :focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }

  /* ─── SKIP TO CONTENT (visible only on keyboard focus) ─── */
  .skip-link {
    position: fixed; top: -100px; left: 16px; z-index: 10000;
    background: var(--accent); color: #0d0d0d; padding: 10px 16px;
    border-radius: 8px; font-size: 14px; font-weight: 600;
    font-family: 'Geist', sans-serif; text-decoration: none;
    transition: top 0.15s var(--ease-out-soft);
  }
  .skip-link:focus, .skip-link:focus-visible { top: 16px; }
`;
